import type { Configuration } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';

// Resolve redirect URIs strictly from per-build env vars; default to localhost for dev
interface ImportMetaEnv {
  VITE_MSAL_REDIRECT_URI?: string;
  VITE_MSAL_POST_LOGOUT_REDIRECT_URI?: string;
  VITE_MSAL_USE_REDIRECT?: string;
}

const resolvedRedirectUri = (import.meta.env as ImportMetaEnv)?.VITE_MSAL_REDIRECT_URI || 'http://localhost:5173';
const resolvedPostLogoutUri = (import.meta.env as ImportMetaEnv)?.VITE_MSAL_POST_LOGOUT_REDIRECT_URI || resolvedRedirectUri;

// MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID || '1fa22273-7439-40d8-9bbe-aced3e4dc0c9',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MSAL_TENANT_ID || '55f0c016-aadc-430f-a7a3-df9b001c0cdb'}`,
    redirectUri: resolvedRedirectUri,
    postLogoutRedirectUri: resolvedPostLogoutUri
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for persistence across tabs
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  }
};

// Create an instance of PublicClientApplication
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL instance immediately
const initializeMsal = async () => {
  try {
    await msalInstance.initialize();
    // Process redirect response (needed if we fall back to loginRedirect)
    try {
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
      }
    } catch (e) {
      console.error('MSAL redirect handling error:', e);
    }
    console.log('MSAL initialized successfully');
  } catch (error) {
    console.error('Error initializing MSAL:', error);
  }
};

// Initialize immediately
initializeMsal();

export { msalInstance };

// Login request configuration. User.Read yields a Graph access token; the ID token
// (returned on the same MSAL response) is what the API uses to identify the user.
export const loginRequest = {
  scopes: ['User.Read'],
};

// Allow choosing redirect vs popup per environment
export const useRedirectFlow = ((import.meta.env as ImportMetaEnv)?.VITE_MSAL_USE_REDIRECT === 'true');

